/**
 * The org feature registry — the single source of truth for what a feature is
 * called, who may see it, where it appears, and in what order.
 *
 * Three surfaces read from this one list, so a feature can no longer be added
 * in one place and go missing in another:
 *
 *   - the site-admin org edit form  (a toggle per entry, grouped by `group`)
 *   - the Power homepage card       (a tile per `surface: 'power'` entry)
 *   - the sidebar                   (ordering for feature-registry-ordered nav
 *                                    sections — see navConfig.jsx)
 *
 * Keep this module free of JSX and component imports: it is imported by the
 * nav access guards on every page, and its invariants are unit-tested with
 * `node --test`. Tile icons live in `orgFeatureIcons.js`.
 */
// Relative paths with explicit extensions (rather than the `@/` alias) so this
// module loads under plain `node --test` as well as through Vite.
import {
    ADMIN_ROLES,
    MEMBER_ROLES,
    SLT_ROLES,
    STAFF_VIEW_ROLES
} from '../../../lib/roles.js'
import { FLAVOURS } from '../../../lib/terminology.js'

/**
 * Display groups, cut by what the feature is *for* rather than when it shipped.
 *
 * The same groups head the site-admin toggle list and the Power homepage card,
 * so an admin deciding "does this org need X" and a user scanning the homepage
 * see the same shape. Order here is the order the groups render in; `order` on
 * each flag positions it within its group.
 */
export const FEATURE_FLAG_GROUPS = [
    {
        key: 'understand',
        label: 'Understand',
        description:
            'See what is being used, when, and how it compares. (Overview is always on and has no toggle.)'
    },
    {
        key: 'act',
        label: 'Act',
        description:
            'Set goals, take action, and get told when something changes.'
    },
    {
        key: 'engage',
        label: 'Engage',
        description:
            'Bring other people in: everyone who uses your buildings, plus building-level detail.'
    },
    {
        key: 'costs',
        label: 'Costs',
        description:
            'Money: tariffs, bills, budgets, and the subscription itself.'
    },
    {
        key: 'report',
        label: 'Report',
        description:
            'Get the numbers out: scheduled digests and formal reports.'
    },
    {
        key: 'manage',
        label: 'Manage',
        description:
            'Operational setup and sections that sit outside the Power dashboard.'
    }
]

export const FEATURE_FLAG_GROUP_KEYS = FEATURE_FLAG_GROUPS.map(
    (group) => group.key
)

/**
 * Where a feature gets a tile on the dashboard homepage.
 *
 * 'power' → a tile on the Power homepage card.
 * 'none'  → deliberately no tile. Use for features that are a settings panel
 *           rather than a page (`scheduled_reports`), that gate a whole card of
 *           their own (`waste`), or that live outside the org dashboard
 *           entirely (`portfolio`, `billing_self_service`). Recording the
 *           intent here is the point — an absent tile is now a decision rather
 *           than an oversight.
 */
export const FEATURE_SURFACES = ['power', 'none']

/**
 * Canonical list of org-level features.
 *
 * These map to the `feature_flags` JSON field on the Org model (snake_case key
 * → boolean) and must stay in sync with `KNOWN_FEATURE_KEYS` on the backend.
 * Gating is opt-in: a key that is absent or false hides the feature; only an
 * explicit `true` enables it.
 *
 * Fields:
 *   key         Flag key. Must match `requiresFeatureFlag` in `routeConfig.jsx`.
 *   label       Default display name; `termKey` overrides it per org flavour.
 *   group       A `FEATURE_FLAG_GROUPS` key.
 *   order       Sort position across BOTH the homepage tiles and the sidebar.
 *               Sparse (10s) and grouped in 100s by `group` so a new feature can
 *               be slotted in without renumbering.
 *   surface     Which homepage card shows a tile — see `FEATURE_SURFACES`.
 *   path        The page the feature enables (also the site-admin preview link).
 *   roles       Who sees the tile/nav entry. Mirrors the route's guard.
 *   flavours    Org flavours the feature is *typically* relevant to. A hint
 *               only: the site-admin form files off-flavour toggles under a
 *               collapsed section but never blocks enabling them, and nothing
 *               is enforced backend-side. Changing an org's flavour therefore
 *               never invalidates flags it already has on.
 *   alwaysOn    No toggle in site admin — the feature cannot be turned off.
 *   tagline     Short user-facing blurb for the homepage tile. Either a string, or
 *               `(t) => string` taking the terminology resolver when the wording differs
 *               by org flavour (a business closes Sites; a school breaks up for holidays).
 *   description Longer site-admin help text explaining what enabling does.
 */
export const ORG_FEATURE_FLAGS = [
    // ---- Understand (100s) ----------------------------------------------
    {
        key: 'overview',
        label: 'Overview',
        group: 'understand',
        order: 100,
        surface: 'power',
        path: '/power',
        roles: MEMBER_ROLES,
        flavours: FLAVOURS,
        alwaysOn: true,
        tagline:
            'How are you performing? Bring the numbers to life with maps and visualisations',
        description:
            'The Power Overview landing page. Always on for every organisation.'
    },
    {
        key: 'insights',
        label: 'Insights',
        group: 'understand',
        order: 110,
        surface: 'power',
        path: '/power/insights',
        roles: SLT_ROLES,
        flavours: FLAVOURS,
        tagline:
            'Deep-dive into your energy patterns: baseload, carbon, weather, and benchmarking',
        description: 'Show the Power → Insights page for this organisation.'
    },
    {
        key: 'timeline',
        label: 'Timeline Map',
        group: 'understand',
        order: 120,
        surface: 'power',
        path: '/power/timeline',
        roles: MEMBER_ROLES,
        flavours: FLAVOURS,
        tagline:
            'Watch consumption rise and fall across the map as you play through time',
        description:
            'Show the Power → Timeline Map page (consumption animated across the map over time) for this organisation.'
    },
    {
        key: 'explore',
        label: 'Explore Data',
        group: 'understand',
        order: 130,
        surface: 'power',
        path: '/power/explore',
        roles: SLT_ROLES,
        flavours: FLAVOURS,
        tagline: 'Slice, explore, and download the raw readings',
        description:
            'Show the Power → Explore Data page (slice and download raw readings, SLT only) for this organisation.'
    },
    {
        key: 'houses',
        label: 'Houses',
        // Flag labels render through the terminology dictionary in the org edit
        // form (getTerm with the EDITED org's flavour); descriptions keep school
        // wording for now.
        termKey: 'houses',
        group: 'understand',
        order: 140,
        surface: 'power',
        path: '/power/houses',
        roles: MEMBER_ROLES,
        // A single household has no sub-buildings to break down.
        flavours: ['school', 'university', 'estate', 'business'],
        tagline: (t) => t('housesTagline'),
        description:
            'Show the Power → Houses summary page for this organisation.'
    },
    {
        key: 'house_details',
        label: 'House Details',
        termKey: 'houseDetailsTitle',
        group: 'understand',
        order: 150,
        surface: 'power',
        path: '/power/house-details',
        roles: SLT_ROLES,
        flavours: ['school', 'university', 'estate', 'business'],
        tagline: (t) => t('houseDetailsTagline'),
        description:
            'Show the Power → House Details deep-dive pages (per-house data, SLT only) for this organisation.'
    },
    {
        key: 'benchmarking',
        label: 'Peer Benchmarking',
        group: 'understand',
        order: 170,
        surface: 'power',
        path: '/power/insights',
        roles: SLT_ROLES,
        // A household has no meaningful anonymised cohort to sit in.
        flavours: ['school', 'university', 'estate', 'business'],
        tagline:
            'See how you compare against similar organisations, anonymously',
        description:
            'Show anonymised peer comparison (cohort-matched by segment and size band). Requires the organisation to opt in to sharing its own anonymised figures, and a cohort of at least 3 other consenting organisations before any peer number is shown.'
    },
    {
        key: 'assistant',
        label: 'Assistant',
        group: 'understand',
        order: 160,
        // Site-admin-only while the tenancy boundary is being proven, so no
        // homepage tile yet. The endpoints require site admin server-side in
        // addition to this flag — the role check is scaffolding that comes out
        // when real users arrive; the flag is permanent.
        surface: 'none',
        path: '/power/assistant',
        roles: ADMIN_ROLES,
        flavours: FLAVOURS,
        description:
            'Show the Power → Assistant panel (ask questions about this organisation’s energy data). Currently reaches site admins only, regardless of this flag.'
    },

    // ---- Act (200s) ------------------------------------------------------
    {
        key: 'targets',
        label: 'Reduction Targets',
        termKey: 'targetsTitle',
        group: 'act',
        order: 200,
        surface: 'power',
        path: '/power/targets',
        roles: MEMBER_ROLES,
        flavours: FLAVOURS,
        tagline: 'Set a % cut and track progress against it week by week',
        description:
            'Show the Power → Reduction Targets page (per-fuel annual % reduction goals with weekly progress). On by default for schools.'
    },
    {
        key: 'education',
        label: 'Activities',
        termKey: 'activitiesTitle',
        group: 'act',
        order: 210,
        surface: 'power',
        path: '/power/activities',
        roles: MEMBER_ROLES,
        // The content library is written around pupils and classroom delivery.
        flavours: ['school'],
        tagline: 'Things to do to cut usage, and a record of what you did',
        description:
            'Show the Power → Activities page (the education content library: pupil activities, staff actions and completion recording). On by default for schools.'
    },
    {
        key: 'alerts',
        label: 'Alerts',
        group: 'act',
        order: 220,
        surface: 'power',
        path: '/alerts',
        roles: STAFF_VIEW_ROLES,
        flavours: FLAVOURS,
        tagline: 'Get told when usage spikes or something looks wrong',
        description:
            'Show the Alerts pages (overview, alert rules, notifications) for this organisation.'
    },
    {
        key: 'holidays',
        label: 'Holidays',
        termKey: 'holidaysTitle',
        group: 'act',
        order: 230,
        surface: 'power',
        path: '/power/holidays',
        roles: SLT_ROLES,
        // Presented as "Away periods" for home and "Closures" for business. An estate is
        // excluded: its voids arise per property rather than on a shared calendar, whereas a
        // trading business closes on shared dates (bank holidays, refurbishments).
        flavours: ['school', 'university', 'home', 'business'],
        tagline: (t) => t('holidaysTagline'),
        description: 'Show the Power → Holidays page for this organisation.'
    },

    // ---- Engage (300s) ---------------------------------------------------
    {
        key: 'energy_quiz',
        label: 'Energy Quiz',
        termKey: 'energyQuizTitle',
        group: 'engage',
        order: 300,
        surface: 'power',
        path: '/power/quiz',
        roles: MEMBER_ROLES,
        flavours: ['school'],
        tagline: 'Take the weekly challenge and top the leaderboard',
        description:
            'Show the Power → Energy Quiz: a weekly challenge, practice rounds and a question of the day built from your own meter data, plus class mode and teacher insights for staff.'
    },
    {
        key: 'class_projects',
        label: 'Class Projects',
        termKey: 'classProjectsTitle',
        group: 'engage',
        order: 310,
        surface: 'power',
        path: '/power/class-projects',
        roles: MEMBER_ROLES,
        flavours: ['school'],
        tagline: 'Download datasets to analyse in class',
        description:
            'Show the Power → Class Projects page (downloadable datasets for classroom analysis) for this organisation.'
    },

    // ---- Costs (400s) ----------------------------------------------------
    {
        key: 'finance',
        label: 'Finance (tariffs & costs)',
        group: 'costs',
        order: 400,
        surface: 'power',
        path: '/admin/finance',
        roles: ADMIN_ROLES,
        flavours: FLAVOURS,
        tagline: 'Set tariffs, see energy costs, and validate supplier bills',
        description:
            'Show the Admin → Finance page (tariffs, bill validation) and the cost figure on the Power Overview for this organisation.'
    },
    {
        key: 'budgets',
        label: 'Budgets',
        group: 'costs',
        order: 410,
        surface: 'power',
        path: '/admin/budgets',
        roles: ADMIN_ROLES,
        flavours: FLAVOURS,
        tagline:
            'Track spend against budget with a projected year-end position',
        description:
            'Show the Admin → Budgets page (budget-vs-actual and forecasts) for this organisation.'
    },
    {
        key: 'billing_self_service',
        label: 'Billing self-service',
        group: 'costs',
        order: 420,
        // Account administration rather than an energy feature — reachable from
        // Admin, deliberately not a Power homepage tile.
        surface: 'none',
        path: '/admin/billing',
        roles: ADMIN_ROLES,
        flavours: FLAVOURS,
        description:
            'Show the Admin → Billing page (Stripe subscribe/upgrade/downgrade, payment & invoice portal) for this organisation.'
    },

    // ---- Report (500s) ---------------------------------------------------
    {
        key: 'carbon_reporting',
        label: 'Carbon Report',
        group: 'report',
        order: 500,
        surface: 'power',
        path: '/power/carbon-report',
        roles: MEMBER_ROLES,
        // SECR is a company reporting obligation; it does not apply to a household.
        flavours: ['school', 'university', 'estate', 'business'],
        tagline:
            'Generate a SECR-shaped carbon report (PDF or CSV) for the year',
        description:
            'Show the Power → Carbon Report page (SECR-shaped PDF/CSV export) for this organisation.'
    },
    {
        key: 'scheduled_reports',
        label: 'Scheduled Reports',
        group: 'report',
        order: 510,
        // A settings panel, not a page of its own.
        surface: 'none',
        roles: ADMIN_ROLES,
        flavours: FLAVOURS,
        description:
            'Show the email digest subscription and default-audience settings for this organisation.'
    },

    // ---- Manage (600s) ---------------------------------------------------
    {
        key: 'occupancy',
        label: 'Occupancy Schedule',
        group: 'manage',
        order: 600,
        surface: 'power',
        path: '/admin/occupancy',
        roles: ADMIN_ROLES,
        flavours: FLAVOURS,
        tagline:
            'Set occupied hours and away periods that power out-of-hours analysis',
        description:
            'Show the Admin → Occupancy page (occupied-hours schedule and away periods) for this organisation.'
    },
    {
        key: 'waste',
        label: 'Waste and Recycling',
        group: 'manage',
        order: 610,
        // Gates an entire homepage card of its own (WasteHomepageCard), so it
        // gets no tile on the Power card.
        surface: 'none',
        path: '/waste',
        roles: MEMBER_ROLES,
        flavours: FLAVOURS,
        description:
            'Show the entire Waste and Recycling section (dashboard card and pages) for this organisation.'
    },
    {
        key: 'portfolio',
        label: 'Portfolio',
        group: 'manage',
        order: 620,
        // Partner console, not the org dashboard.
        surface: 'none',
        path: '/partner/portfolio',
        flavours: FLAVOURS,
        description:
            'Show the Partner → Portfolio dashboard (cross-org league tables). Only reaches partner admins.'
    },
    {
        key: 'location_access',
        label: 'Location access',
        group: 'manage',
        order: 630,
        // Gates assigning the location member role (site manager / tenant) on the
        // admin users page; not a page of its own, so no homepage tile.
        surface: 'none',
        path: '/admin/users',
        roles: ADMIN_ROLES,
        flavours: ['estate', 'business'],
        description:
            'Let org admins give a person a read-only login for one or more locations only (a site manager for a business, a tenant for an estate). They see those locations and their sub-locations, nothing else.'
    }
]

/** Registry entries keyed by flag key, for O(1) lookup. */
export const FEATURE_FLAGS_BY_KEY = Object.fromEntries(
    ORG_FEATURE_FLAGS.map((flag) => [flag.key, flag])
)

/**
 * Sort position for a feature key. Unknown keys sort last (but keep their
 * relative order, since callers use a stable sort), so a nav entry whose flag
 * is missing from the registry degrades to "at the end" rather than vanishing.
 */
export function featureOrder(key) {
    return FEATURE_FLAGS_BY_KEY[key]?.order ?? Number.MAX_SAFE_INTEGER
}

/** The flags a site admin can toggle — everything except the always-on ones. */
export function getTogglableFlags() {
    return ORG_FEATURE_FLAGS.filter((flag) => !flag.alwaysOn)
}

/**
 * Is this feature typically relevant to `flavour`?
 *
 * A presentation hint only — see the `flavours` field note above. An entry with
 * no `flavours` list is treated as relevant everywhere.
 */
export function isFeatureRelevantToFlavour(flag, flavour) {
    if (!flag?.flavours?.length) {
        return true
    }
    return flag.flavours.includes(flavour)
}

/**
 * Registry entries that should render as tiles on `surface`, in display order.
 *
 * Filters by feature flag, role, and (softly) flavour, so a caller just maps
 * over the result. Pass `hasRole` in rather than importing it to keep this
 * module dependency-free and unit-testable.
 */
export function getSurfaceTiles(
    surface,
    { activeOrg, user, hasRole, respectFlavour = false } = {}
) {
    return ORG_FEATURE_FLAGS.filter((flag) => flag.surface === surface)
        .filter(
            (flag) => flag.alwaysOn || isFeatureFlagEnabled(activeOrg, flag.key)
        )
        .filter((flag) => !flag.roles || !hasRole || hasRole(user, flag.roles))
        .filter(
            (flag) =>
                !respectFlavour ||
                isFeatureRelevantToFlavour(flag, activeOrg?.flavour)
        )
        .sort((a, b) => a.order - b.order)
}

/**
 * Opt-in check used by route/nav guards: the flag must be explicitly `true`.
 *
 * Reads `effective_features` — the backend value clamped to the owning partner's
 * licensed entitlements (see org/services.py `get_effective_features`). For
 * platform-owned orgs `effective_features` equals the raw flags. Falls back to
 * `feature_flags` only if `effective_features` is absent (older API payloads).
 */
export function isFeatureFlagEnabled(activeOrg, flag) {
    const features = activeOrg?.effective_features ?? activeOrg?.feature_flags
    return features?.[flag] === true
}
